import type React from 'react';
import { useMemo } from 'react';

import { createColumnHelper, getCoreRowModel, type TableOptions, useReactTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import EnhancedTable from 'components/tables/enhancedTable/EnhancedTable';
import { BodyRegular } from 'components/typography/Typography';
import { type EventCost } from 'typedefs/event';

import { useStyles } from './styles';
import { calculateToPay, playerName, type PromoteParticipantData } from './utils';

interface PromoteToMainDrawCostsProps {
  participantsToPromote: PromoteParticipantData[];
  eventCosts: EventCost[];
}

interface PromoteToMainDrawCostsData {
  name: string;
  team?: string;
  paid: number;
  toPay: number;
}

const columnHelper = createColumnHelper<PromoteToMainDrawCostsData>();

const PromoteToMainDrawCosts: React.FC<React.PropsWithChildren<PromoteToMainDrawCostsProps>> = ({
  participantsToPromote,
  eventCosts,
}) => {
  const isTeam = participantsToPromote.every((p) => p.isTeam);
  const isDoubles = participantsToPromote.every((p) => p.isDoubles);
  const data = useMemo(() => {
    const playersWithTeam = participantsToPromote.flatMap((participant) =>
      participant.individualParticipants.map((p) => ({ ...p, team: isTeam || isDoubles ? participant.name : '-' })),
    );

    const playersWithAllData = playersWithTeam.map((p) => {
      const currentPlayerCosts = eventCosts.find((ec) => ec.personCsId === p.participantId);
      return { ...p, paid: currentPlayerCosts?.amountPaid, eventFees: currentPlayerCosts?.amountPayableWithEvents };
    });

    return playersWithAllData.map((player) => {
      return {
        name: playerName(player),
        team: player.team,
        paid: Number((player.paid / 100).toFixed(2)),
        toPay: calculateToPay(player.eventFees, player.paid),
      };
    });
  }, [participantsToPromote, isTeam, isDoubles, eventCosts]);

  const columns = useTableColumns();
  const filteredColumns = useMemo(() => columns?.filter((c) => (!isTeam ? c.id !== 'team' : c)), [columns, isTeam]);

  const options: TableOptions<PromoteToMainDrawCostsData> = useMemo(
    () => ({
      columns: filteredColumns,
      data,
      getCoreRowModel: getCoreRowModel(),
    }),
    [filteredColumns, data],
  );

  const tableInstance = useReactTable<PromoteToMainDrawCostsData>(options);
  const rows = tableInstance.getRowModel().rows;

  return <>{data && <EnhancedTable columnsToggle={false} rows={rows} tableInstance={tableInstance} />}</>;
};

const useTableColumns = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  return useMemo(
    () => [
      columnHelper.accessor(({ name }) => name, {
        id: 'name',
        header: t('player'),
        cell: ({ getValue }) => <BodyRegular>{getValue()}</BodyRegular>,
        meta: {
          headerLabel: t('player'),
        },
      }),
      columnHelper.accessor(({ team }) => team, {
        id: 'team',
        header: t('team'),
        cell: ({ getValue }) => <BodyRegular>{getValue()}</BodyRegular>,
        meta: {
          headerLabel: t('team'),
        },
      }),
      columnHelper.accessor(({ paid }) => paid, {
        id: 'paid',
        header: t('amountPaid'),
        cell: ({ getValue }) => <BodyRegular>${getValue()}</BodyRegular>,
        meta: {
          headerLabel: t('amountPaid'),
        },
      }),
      columnHelper.accessor(({ toPay }) => toPay, {
        id: 'toPay',
        header: t('chargeAmount'),
        cell: ({ getValue }) => <BodyRegular className={classes.amountToPay}>${getValue()}</BodyRegular>,
        meta: {
          headerLabel: t('chargeAmount'),
        },
      }),
    ],
    [classes.amountToPay, t],
  );
};
export default PromoteToMainDrawCosts;
